import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ThemeTokens } from '@course-authoring/shared-types';
import { DEFAULT_ASSISTANT_SETTINGS, normalizeAssistantSettings } from '@course-authoring/shared-types';
import type { AssistantSettings, PublishJobDto } from '@course-authoring/shared-types';
import { useLanguageContext } from '../../hooks/useLanguageContext';
import { useClientContext, toScopeAccountId } from '../../hooks/useClientContext';
import { useAccountTheme, useSaveAccountTheme } from '../../api/accountThemeApi';
import type { AccountThemeDto } from '../../api/accountThemeApi';
import { uploadThemeAsset, type ThemeAssetDto } from '../../api/themeAssetApi';
import { fetchPublishJob, retryPublishJob } from '../../api/publishJobApi';
import { PUBLISH_JOB_POLL_INTERVAL_MS } from '../../utils/publishJobPolling';
import { CUSTOM_FONT_SENTINEL } from '../../theme/fontOptions';
import { getThemeDraftComparison, toEffective, toPayload } from './themeSettingsUtils';
import type { ThemeDirtyFields, ThemePayload } from './themeSettingsUtils';
import { useThemeColorSettings, type ThemeColorsSectionViewModel } from './useThemeColorSettings';
import { useThemeLogoSettings, type ThemeLogoSectionViewModel } from './useThemeLogoSettings';
import { useThemeFontSettings, type ThemeFontSectionViewModel } from './useThemeFontSettings';
import { useThemeAssistantSettings, type ThemeAssistantSectionViewModel } from './useThemeAssistantSettings';

export type { ThemeDirtyFields } from './themeSettingsUtils';

export interface ThemeSettingsFormViewModel {
  logo: ThemeLogoSectionViewModel;
  font: ThemeFontSectionViewModel;
  colors: ThemeColorsSectionViewModel;
  assistant: ThemeAssistantSectionViewModel;
  isDirty: boolean;
  isSaving: boolean;
  isLoading: boolean;
  isFormDisabled: boolean;
  loadError: string | null;
  saveError: string | null;
  saved: boolean;
  isSaveUncertain: boolean;
  saveReconciliationError: string | null;
  preparationJob: PublishJobDto | null;
  isPreparationReady: boolean;
  preparationError: string | null;
  isPreparationActive: boolean;
  isPreparationInherited: boolean;
  isPreparationRetrying: boolean;
  retryPreparation: () => Promise<void>;
  dirtyFields: ThemeDirtyFields;
  retryLoad: () => Promise<void>;
  save: () => Promise<boolean>;
  abandonPendingSave: () => void;
}

interface CompletedUpload {
  scopeAccountId: number | null;
  sessionId: number;
  asset: ThemeAssetDto;
}
function errorText(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

export function useThemeSettingsForm(tokens: ThemeTokens): ThemeSettingsFormViewModel {
  const { clientContext } = useClientContext();
  const { selectedLocale } = useLanguageContext();
  const scopeAccountId = toScopeAccountId(clientContext?.id);
  const { data: themeDto, isSuccess, isError, error: queryError, isFetching, isPlaceholderData, refetch } = useAccountTheme(scopeAccountId, { refetchInterval: PUBLISH_JOB_POLL_INTERVAL_MS });
  const { mutateAsync: saveTheme } = useSaveAccountTheme();
  const themeLoaded = isSuccess && !isPlaceholderData;
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [loadErrorOverride, setLoadErrorOverride] = useState<string | null>(null);
  const [preparationJob, setPreparationJob] = useState<PublishJobDto | null>(null);
  const [preparationReadyIdentity, setPreparationReadyIdentity] = useState<string | null>(null);
  const observedActivePreparationRef = useRef<string | null>(null);
  const [preparationError, setPreparationError] = useState<string | null>(null);
  const [isPreparationRetrying, setIsPreparationRetrying] = useState(false);
  const [isSaveUncertain, setIsSaveUncertain] = useState(false);
  const [saveReconciliationError, setSaveReconciliationError] = useState<string | null>(null);
  const [loadedScope, setLoadedScope] = useState<number | null | undefined>(undefined);
  const [baseline, setBaseline] = useState<ThemePayload | null>(null);
  const [baselineCertEnabled, setBaselineCertEnabled] = useState(false);
  const completedUploadsRef = useRef(new Map<File, CompletedUpload>());
  const saveLockRef = useRef(false);
  const hydratedDtoRef = useRef<{ scope: number | null; scopeSessionId: number; dto: AccountThemeDto | undefined } | null>(null);
  const scopeSessionRef = useRef(0);
  const loadedSessionRef = useRef<number | null>(null);
  const sessionRef = useRef(0);
  const priorScopeRef = useRef(scopeAccountId);
  const scopeRef = useRef(scopeAccountId);
  scopeRef.current = scopeAccountId;
  if (priorScopeRef.current !== scopeAccountId) {
    priorScopeRef.current = scopeAccountId;
    sessionRef.current += 1;
    scopeSessionRef.current += 1;
    saveLockRef.current = false;
  }

  const isCurrentScopeSeeded = loadedScope === scopeAccountId && loadedSessionRef.current === scopeSessionRef.current;
  const isLoading = !isCurrentScopeSeeded;
  const isPreparationInherited = Boolean(preparationJob && themeDto?.isInherited === true);
  const isPreparationActive = Boolean(
    preparationJob && (preparationJob.status === 'queued' || preparationJob.status === 'running')
    && !isPreparationInherited && preparationJob.externalAccountId === scopeAccountId,
  );
  const baseDisabled = isLoading || isSaving || isPreparationActive;
  const fontSettings = useThemeFontSettings(scopeAccountId, baseDisabled);
  const isFormDisabled = baseDisabled || fontSettings.isFontProcessing;
  const colorSettings = useThemeColorSettings(tokens, isFormDisabled);
  const logoSettings = useThemeLogoSettings(
    scopeAccountId,
    colorSettings.logoBackgroundColor,
    colorSettings.certBackgroundColor,
    isFormDisabled,
  );
  const assistantSettings = useThemeAssistantSettings(isFormDisabled);
  const font = fontSettings.sectionViewModel;
  const logo = logoSettings.sectionViewModel;

  const normalizedPayload = useCallback((dto: AccountThemeDto | undefined) => {
    const { colors, builtInFontFamily } = toEffective(dto, colorSettings.themeFloor);
    const fontFamily = dto?.fontFamily === CUSTOM_FONT_SENTINEL
      ? CUSTOM_FONT_SENTINEL
      : builtInFontFamily;
    const hasExpandedCustomFont = fontFamily === CUSTOM_FONT_SENTINEL && dto?.customFont != null;
    return {
      ...toPayload(
        colors,
        hasExpandedCustomFont ? CUSTOM_FONT_SENTINEL : builtInFontFamily,
        dto?.primaryLogo?.id ?? null,
        dto?.certificateLogoAssetId ? dto.certificateLogo?.id ?? null : null,
        // The retained custom font stays part of the saved state while a built-in
        // font is selected (NA-454), so it must not read as an unsaved change.
        dto?.customFont?.id ?? null,
      ),
      assistant: normalizeAssistantSettings(dto?.assistant ?? DEFAULT_ASSISTANT_SETTINGS),
      assistantAvatarAssetId: dto?.assistantAvatarAssetId ?? dto?.assistantAvatar?.id ?? null,
    };
  }, [colorSettings.themeFloor]);

  const currentPayload = useMemo(() => ({
    ...toPayload(
      colorSettings.colors,
      fontSettings.fontFamilyForPayload,
      logoSettings.primaryLogoAssetId,
      logoSettings.certificateLogoAssetId,
      fontSettings.customFontAssetIdForPayload,
    ),
    assistant: assistantSettings.settings,
    assistantAvatarAssetId: assistantSettings.avatarAssetId,
  }), [colorSettings.colors, fontSettings.fontFamilyForPayload, fontSettings.customFontAssetIdForPayload, logoSettings.primaryLogoAssetId, logoSettings.certificateLogoAssetId, assistantSettings.settings, assistantSettings.avatarAssetId]);
  const draftComparison = useMemo(() => getThemeDraftComparison(
    currentPayload,
    baseline,
    {
      primaryLogo: Boolean(logoSettings.primaryLogoFile),
      certificateLogo: Boolean(logoSettings.certificateLogoFile),
      customFont: Boolean(fontSettings.stagedFontFile),
      assistantAvatar: Boolean(assistantSettings.avatarFile),
    },
    logo.useSeparateCertLogo,
    baselineCertEnabled,
  ), [currentPayload, baseline, logoSettings.primaryLogoFile, logoSettings.certificateLogoFile, fontSettings.stagedFontFile, assistantSettings.avatarFile, logo.useSeparateCertLogo, baselineCertEnabled]);
  // Hydration keeps its existing trigger set: a color/font revert alone must not apply
  // a refetch that was deferred while the author had unsaved changes.
  const draftComparisonRef = useRef(draftComparison);
  draftComparisonRef.current = draftComparison;

  const seedFromDto = useCallback((dto: AccountThemeDto | undefined, scope: number | null) => {
    hydratedDtoRef.current = { scope, scopeSessionId: scopeSessionRef.current, dto };
    loadedSessionRef.current = scopeSessionRef.current;
    const effective = toEffective(dto, colorSettings.themeFloor);
    const seededPayload = normalizedPayload(dto);
    colorSettings.setColors(effective.colors);
    fontSettings.seedFontFromDto(dto, effective.builtInFontFamily);
    logoSettings.seedLogosFromDto(dto);
    assistantSettings.seedFromDto(dto);
    setBaseline(seededPayload);
    setBaselineCertEnabled(Boolean(dto?.certificateLogoAssetId));
    setSaveError(null);
    setLoadedScope(scope);
    setLoadErrorOverride(null);
  }, [colorSettings.themeFloor, colorSettings.setColors, fontSettings.seedFontFromDto, logoSettings.seedLogosFromDto, assistantSettings.seedFromDto, normalizedPayload]);

  useEffect(() => {
    setIsSaving(false);
    setSaveError(null);
    setSaved(false);
    setLoadErrorOverride(null);
    completedUploadsRef.current.clear();
    hydratedDtoRef.current = null;
    loadedSessionRef.current = null;
    setLoadedScope(undefined);
    setPreparationJob(null);
    setPreparationReadyIdentity(null);
    observedActivePreparationRef.current = null;
    setPreparationError(null);
    setIsPreparationRetrying(false);
    setIsSaveUncertain(false);
    setSaveReconciliationError(null);
  }, [scopeAccountId]);

  useEffect(() => {
    setPreparationReadyIdentity(null);
    observedActivePreparationRef.current = null;
  }, [selectedLocale]);

  useEffect(() => {
    if (!themeLoaded) return;
    const incoming = themeDto?.publishJob ?? null;
    setPreparationJob((current) => {
      if (!incoming) return null;
      if (current?.id === incoming.id && Date.parse(current.updatedAt) > Date.parse(incoming.updatedAt)) return current;
      return incoming;
    });
    if (incoming && (incoming.status === 'queued' || incoming.status === 'running')) {
      observedActivePreparationRef.current = `${incoming.id}:${incoming.attempt}`;
    }
  }, [themeLoaded, themeDto]);

  useEffect(() => {
    if (!preparationJob) return;
    const identity = `${preparationJob.id}:${preparationJob.attempt}`;
    if (preparationJob.status === 'queued' || preparationJob.status === 'running') {
      observedActivePreparationRef.current = identity;
    } else if (preparationJob.status === 'completed' && observedActivePreparationRef.current === identity) {
      setPreparationReadyIdentity(identity);
      observedActivePreparationRef.current = null;
    }
  }, [preparationJob, selectedLocale]);

  useEffect(() => {
    if (!preparationJob || (preparationJob.status !== 'queued' && preparationJob.status !== 'running')) return;
    const pollScopeSession = scopeSessionRef.current;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const poll = async () => {
      try {
        const refreshed = await fetchPublishJob(preparationJob.id);
        if (cancelled || pollScopeSession !== scopeSessionRef.current) return;
        setPreparationJob(refreshed);
        setPreparationError(null);
        if (refreshed.status === 'queued' || refreshed.status === 'running') {
          timer = setTimeout(() => { void poll(); }, PUBLISH_JOB_POLL_INTERVAL_MS);
        }
      } catch (error) {
        if (cancelled || pollScopeSession !== scopeSessionRef.current) return;
        setPreparationError(errorText(error, 'Failed to refresh preparation status.'));
        timer = setTimeout(() => { void poll(); }, PUBLISH_JOB_POLL_INTERVAL_MS);
      }
    };
    void poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [preparationJob?.id, preparationJob?.status]);

  useEffect(() => {
    if (!themeLoaded) return;
    if (hydratedDtoRef.current?.scope === scopeAccountId
      && hydratedDtoRef.current.scopeSessionId === scopeSessionRef.current
      && hydratedDtoRef.current.dto === themeDto) return;
    if (isCurrentScopeSeeded && isFetching) return;
    const hasLocalValidationError = Boolean(
      font.fontError || logo.logoError || logo.certLogoError || logo.certificateSaveError || assistantSettings.avatarError,
    );
    if (isCurrentScopeSeeded && (
      isSaving || fontSettings.isFontProcessing || hasLocalValidationError || saveError || baseline === null
    )) return;
    if (isCurrentScopeSeeded && !isFetching) {
      if (draftComparisonRef.current.isDirty) return;
    }
    seedFromDto(themeDto, scopeAccountId);
  }, [themeLoaded, themeDto, scopeAccountId, isCurrentScopeSeeded, isSaving, fontSettings.isFontProcessing, font.fontError, logo.logoError, logo.certLogoError, logo.certificateSaveError, assistantSettings.avatarError, saveError, baseline, isFetching, seedFromDto, logo.useSeparateCertLogo, baselineCertEnabled]);

  useEffect(() => () => {
    sessionRef.current += 1;
    saveLockRef.current = false;
    completedUploadsRef.current.clear();
  }, []);

  useEffect(() => {
    if (isError) setLoadErrorOverride(errorText(queryError, 'Failed to load theme settings.'));
  }, [isError, queryError]);

  const retryLoad = useCallback(async () => {
    setLoadErrorOverride(null);
    setSaved(false);
    const retrySession = sessionRef.current;
    const result = await refetch();
    if (sessionRef.current !== retrySession) return;
    if (result.isError) setLoadErrorOverride(errorText(result.error, 'Failed to load theme settings.'));
    else if (result.data) {
      setIsSaveUncertain(false);
      setSaveReconciliationError(null);
      if (result.data.publishJob) setPreparationJob(result.data.publishJob);
    }
  }, [refetch]);

  const dirtyFields = draftComparison.dirtyFields;
  const isDirty = !isLoading && draftComparison.isDirty;
  useEffect(() => {
    if (isDirty && saved) setSaved(false);
  }, [isDirty, saved]);
  useEffect(() => {
    if (isDirty) setPreparationReadyIdentity(null);
  }, [isDirty]);

  const save = useCallback(async (): Promise<boolean> => {
    if (saveLockRef.current || isFormDisabled || isSaving || isSaveUncertain || !isDirty || !baseline) return false;
    if (logo.useSeparateCertLogo && !logoSettings.certificateLogoFile && !logoSettings.certificateLogoAssetId) {
      logoSettings.setCertificateSaveError('Upload a certificate logo or turn off ‘Use a different logo.’');
      return false;
    }
    logoSettings.setCertificateSaveError(null);
    setSaved(false);
    setSaveError(null);
    saveLockRef.current = true;
    setIsSaving(true);
    const saveSession = sessionRef.current;
    const saveScope = scopeAccountId;
    const capturedColors = { ...colorSettings.colors };
    const capturedFontFamily = fontSettings.fontFamilyForPayload;
    const capturedFontAssetId = fontSettings.customFontAssetIdForPayload;
    const primaryFile = logoSettings.primaryLogoFile;
    const certificateFile = logoSettings.certificateLogoFile;
    const fontFile = fontSettings.stagedFontFile;
    const avatarFile = assistantSettings.avatarFile;
    const capturedAssistant: AssistantSettings = { ...assistantSettings.settings };
    const sameSession = () => sessionRef.current === saveSession && scopeRef.current === saveScope;
    const uploaded = async (file: File, kind: 'logo' | 'font'): Promise<ThemeAssetDto> => {
      const existing = completedUploadsRef.current.get(file);
      if (existing?.scopeAccountId === saveScope && existing.sessionId === saveSession) return existing.asset;
      const asset = await uploadThemeAsset({ externalAccountId: saveScope, kind, file });
      if (sameSession()) completedUploadsRef.current.set(file, { scopeAccountId: saveScope, sessionId: saveSession, asset });
      return asset;
    };
    let themeWriteStarted = false;
    try {
      let primaryId = logoSettings.primaryLogoAssetId;
      let certificateId = logoSettings.certificateLogoAssetId;
      let customFontId = capturedFontAssetId;
      let assistantAvatarId = assistantSettings.avatarAssetId;
      if (primaryFile) primaryId = (await uploaded(primaryFile, 'logo')).id;
      if (!sameSession()) return false;
      if (logo.useSeparateCertLogo && certificateFile) certificateId = (await uploaded(certificateFile, 'logo')).id;
      if (!sameSession()) return false;
      if (fontFile) customFontId = (await uploaded(fontFile, 'font')).id;
      if (!sameSession()) return false;
      if (avatarFile) assistantAvatarId = (await uploaded(avatarFile, 'logo')).id;
      if (!sameSession()) return false;
      const payload = {
        ...toPayload(capturedColors, capturedFontFamily, primaryId, logo.useSeparateCertLogo ? certificateId : null, customFontId),
        assistant: capturedAssistant,
        assistantAvatarAssetId: assistantAvatarId,
      };
      themeWriteStarted = true;
      const responseDto = await saveTheme({ externalAccountId: saveScope, ...payload });
      if (!sameSession()) return false;
      seedFromDto(responseDto, saveScope);
      const returnedJob = responseDto.publishJob ?? null;
      const returnedJobIdentity = returnedJob ? `${returnedJob.id}:${returnedJob.attempt}` : null;
      const admittedJob = responseDto.admittedPublishJob;
      setPreparationReadyIdentity(returnedJob?.status === 'completed'
        && returnedJob.id === admittedJob?.id && returnedJob.attempt === admittedJob.attempt
        ? returnedJobIdentity : null);
      observedActivePreparationRef.current = returnedJob && (returnedJob.status === 'queued' || returnedJob.status === 'running')
        ? returnedJobIdentity
        : null;
      setPreparationJob(returnedJob);
      setPreparationError(null);
      completedUploadsRef.current.clear();
      setSaved(true);
      return true;
    } catch (error) {
      if (sameSession()) {
        setSaveError(errorText(error, 'Failed to save theme settings. Please try again.'));
        const conflictJob = typeof error === 'object' && error !== null && 'publishJob' in error
          ? error.publishJob
          : null;
        if (conflictJob && typeof conflictJob === 'object' && 'id' in conflictJob) {
          setPreparationJob(conflictJob as PublishJobDto);
        } else if (themeWriteStarted) {
          const hasHttpResponse = typeof error === 'object' && error !== null && 'status' in error
            && typeof error.status === 'number' && error.status > 0;
          if (!hasHttpResponse) {
            setIsSaveUncertain(true);
            setSaveReconciliationError('Checking whether the save completed…');
          }
          // Reconcile an uncertain/lost PUT response before another Save can be attempted.
          try {
            const reconciled = await refetch();
            if (sameSession()) {
              if (reconciled.data?.publishJob) setPreparationJob(reconciled.data.publishJob);
              if (!hasHttpResponse) {
                if (!reconciled.isError && reconciled.data) {
                  setIsSaveUncertain(false);
                  setSaveReconciliationError(null);
                } else {
                  setSaveReconciliationError('Could not verify whether the theme was saved. Use Retry load before saving again.');
                }
              }
            }
          } catch {
            if (sameSession() && !hasHttpResponse) {
              setSaveReconciliationError('Could not verify whether the theme was saved. Use Retry load before saving again.');
            }
          }
        }
      }
      return false;
    } finally {
      if (sameSession()) {
        saveLockRef.current = false;
        setIsSaving(false);
      }
    }
  }, [isFormDisabled, isSaving, isSaveUncertain, isDirty, baseline, logo.useSeparateCertLogo, logoSettings.certificateLogoFile, logoSettings.certificateLogoAssetId, logoSettings.setCertificateSaveError, scopeAccountId, colorSettings.colors, fontSettings.fontFamilyForPayload, fontSettings.customFontAssetIdForPayload, logoSettings.primaryLogoFile, logoSettings.primaryLogoAssetId, logoSettings.certificateLogoFile, logoSettings.certificateLogoAssetId, fontSettings.stagedFontFile, assistantSettings.avatarFile, assistantSettings.settings, assistantSettings.avatarAssetId, saveTheme, seedFromDto, refetch]);

  const retryPreparation = useCallback(async () => {
    if (!preparationJob || preparationJob.status !== 'failed' || isPreparationInherited) return;
    if (isDirty) {
      setPreparationError('Save your newer draft first, then retry preparation.');
      return;
    }
    setPreparationError(null);
    setIsPreparationRetrying(true);
    const retrySession = scopeSessionRef.current;
    try {
      const retried = await retryPublishJob(preparationJob.id);
      if (retrySession !== scopeSessionRef.current || retried.externalAccountId !== scopeAccountId) return;
      setPreparationReadyIdentity(null);
      observedActivePreparationRef.current = `${retried.id}:${retried.attempt}`;
      setPreparationJob(retried);
    } catch (error) {
      if (retrySession === scopeSessionRef.current) setPreparationError(errorText(error, 'Failed to retry preparation.'));
    } finally {
      if (retrySession === scopeSessionRef.current) setIsPreparationRetrying(false);
    }
  }, [preparationJob, isPreparationInherited, isDirty, scopeAccountId]);

  const abandonPendingSave = useCallback(() => {
    sessionRef.current += 1;
    saveLockRef.current = false;
    setIsSaving(false);
    setSaveError(null);
    setSaved(false);
    setIsSaveUncertain(false);
    setSaveReconciliationError(null);
    completedUploadsRef.current.clear();
  }, []);

  return {
    logo,
    font,
    colors: colorSettings.sectionViewModel,
    assistant: assistantSettings,
    isDirty,
    isSaving,
    isLoading,
    isFormDisabled,
    loadError: loadErrorOverride,
    saveError,
    saved,
    isSaveUncertain,
    saveReconciliationError,
    preparationJob,
    isPreparationReady: Boolean(preparationJob && preparationReadyIdentity === `${preparationJob.id}:${preparationJob.attempt}`),
    preparationError,
    isPreparationActive,
    isPreparationInherited,
    isPreparationRetrying,
    retryPreparation,
    dirtyFields,
    retryLoad,
    save,
    abandonPendingSave,
  };
}
